import { useState, useEffect } from "react";
import { parseISO, format } from "date-fns";
import {
  Settings, Plus, Edit, Trash2, DollarSign, CheckCircle2, XCircle,
  Clock, Users, CreditCard, ExternalLink, AlertTriangle, TrendingUp,
  Building2, RefreshCw, BanknoteIcon,
} from "lucide-react";
import {
  Buildings as PhBuildings,
  Users as PhUsers,
  CurrencyDollar as PhCurrencyDollar,
  Clock as PhClock,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatCard } from "@/components/StatCard";
import {
  useAmenities, usePendingAmenityBookings, useMyAmenityBookings,
  useApproveAmenityBooking, useRejectAmenityBooking,
  useManageAmenity, useDeleteAmenity, useAmenityRevenue,
  type Amenity, type AmenityBooking,
} from "@/hooks/useAmenities";
import {
  useConnectStatus, useCreateConnectAccount, useConnectOnboardingLink,
} from "@/hooks/useStripeConnect";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";

const COLORS = [
  "#4f46e5", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4",
];

const EMPTY_AMENITY = {
  name: "", description: "", capacity: "10", max_hours: "4",
  color: "#4f46e5", location: "", rules: "",
  requires_payment: false, require_approval: true,
  price_per_hour: "", price_flat: "",
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    approved: "bg-green-100 text-green-800 border-green-200",
    pending:  "bg-amber-100 text-amber-800 border-amber-200",
    cancelled:"bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${map[status] ?? "bg-muted text-muted-foreground"}`}>
      {status}
    </span>
  );
}

function PaymentStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    paid:     "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending:  "bg-blue-100 text-blue-800 border-blue-200",
    unpaid:   "bg-slate-100 text-slate-800 border-slate-200",
    waived:   "bg-slate-100 text-slate-600 border-slate-200",
    refunded: "bg-purple-100 text-purple-800 border-purple-200",
    failed:   "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${map[status] ?? "bg-muted text-muted-foreground"}`}>
      {status}
    </span>
  );
}

export default function AmenityManagement() {
  const { user, activeOrgId } = useAuth();
  const [searchParams] = useSearchParams();

  const isAdminOrPM =
    user?.role === "strata_admin" ||
    user?.role === "property_manager" ||
    user?.role === "system_admin";

  // ── Data ───────────────────────────────────────────────────────────────────
  const { data: amenities = [], isLoading: amenitiesLoading } = useAmenities(true);
  const { data: pendingBookings = [] } = usePendingAmenityBookings();
  const { data: myBookings = [], isLoading: myBookingsLoading } = useMyAmenityBookings();
  const { data: revenue = [] } = useAmenityRevenue();
  const { data: connectStatus, isLoading: connectLoading, refetch: refetchConnect } = useConnectStatus();

  const manageAmenity = useManageAmenity();
  const deleteAmenity = useDeleteAmenity();
  const approveBooking = useApproveAmenityBooking();
  const rejectBooking = useRejectAmenityBooking();
  const createConnectAccount = useCreateConnectAccount();
  const getOnboardingLink = useConnectOnboardingLink();

  // ── State ──────────────────────────────────────────────────────────────────
  const [showAmenityDialog, setShowAmenityDialog] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [amenityForm, setAmenityForm] = useState(EMPTY_AMENITY);
  const [rejectTarget, setRejectTarget] = useState<AmenityBooking | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Handle Stripe Connect return redirect
  useEffect(() => {
    const connectParam = searchParams.get("connect");
    if (connectParam === "success" || connectParam === "refresh") {
      // Refresh the connect status from Stripe
      createConnectAccount.mutate(activeOrgId ?? undefined);
      refetchConnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const totalRevenue = revenue.reduce((s, r) => s + Number(r.total_revenue), 0);
  const pendingRevenue = revenue.reduce((s, r) => s + Number(r.pending_revenue), 0);
  const totalBookings = revenue.reduce((s, r) => s + Number(r.total_bookings), 0);

  // ── Amenity form helpers ───────────────────────────────────────────────────
  function openEditAmenity(a: Amenity) {
    setEditingAmenity(a);
    setAmenityForm({
      name: a.name, description: a.description ?? "",
      capacity: String(a.capacity), max_hours: String(a.max_hours),
      color: a.color, location: a.location ?? "", rules: a.rules ?? "",
      requires_payment: a.requires_payment, require_approval: a.require_approval,
      price_per_hour: a.price_per_hour != null ? String(a.price_per_hour) : "",
      price_flat: a.price_flat != null ? String(a.price_flat) : "",
    });
    setShowAmenityDialog(true);
  }

  function openNewAmenity() {
    setEditingAmenity(null);
    setAmenityForm(EMPTY_AMENITY);
    setShowAmenityDialog(true);
  }

  async function handleSaveAmenity() {
    await manageAmenity.mutateAsync({
      id: editingAmenity?.id,
      name: amenityForm.name,
      description: amenityForm.description || null,
      capacity: parseInt(amenityForm.capacity) || 10,
      max_hours: parseInt(amenityForm.max_hours) || 4,
      color: amenityForm.color,
      location: amenityForm.location || null,
      rules: amenityForm.rules || null,
      requires_payment: amenityForm.requires_payment,
      require_approval: amenityForm.require_approval,
      price_per_hour: amenityForm.requires_payment && amenityForm.price_per_hour
        ? parseFloat(amenityForm.price_per_hour) : null,
      price_flat: amenityForm.requires_payment && amenityForm.price_flat
        ? parseFloat(amenityForm.price_flat) : null,
      is_active: true,
    });
    setShowAmenityDialog(false);
  }

  async function handleConnectStripe() {
    if (!connectStatus?.account_id) {
      const result = await createConnectAccount.mutateAsync(activeOrgId ?? undefined);
      if (!result.onboarded) {
        const link = await getOnboardingLink.mutateAsync(activeOrgId ?? undefined);
        if (link.url) window.location.href = link.url;
      }
    } else {
      const link = await getOnboardingLink.mutateAsync(activeOrgId ?? undefined);
      if (link.url) window.location.href = link.url;
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Amenity Management</h1>
          <p className="text-sm text-muted-foreground">
            Manage amenities, review bookings, and configure payouts
          </p>
        </div>
        <Button size="sm" onClick={openNewAmenity}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Amenity
        </Button>
      </div>

      {/* Stripe Connect Banner */}
      {!connectLoading && (
        <Card className={`border ${connectStatus?.onboarded ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
          <CardContent className="py-3 px-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`rounded-full p-2 ${connectStatus?.onboarded ? "bg-green-100" : "bg-amber-100"}`}>
                <BanknoteIcon className={`h-4 w-4 ${connectStatus?.onboarded ? "text-green-700" : "text-amber-700"}`} />
              </div>
              <div>
                <p className="text-sm font-semibold">
                  {connectStatus?.onboarded
                    ? "✓ Bank Account Connected — Payouts Active"
                    : "Connect Bank Account to Receive Payments"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {connectStatus?.onboarded
                    ? "Amenity booking payments are transferred directly to your bank account via Stripe."
                    : "Set up your Stripe Connect account so guests can pay for amenity bookings. Money goes directly to your bank."}
                </p>
              </div>
            </div>
            {connectStatus?.onboarded ? (
              <Button
                size="sm"
                variant="outline"
                onClick={handleConnectStripe}
                disabled={getOnboardingLink.isPending}
                className="shrink-0 border-green-300"
              >
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                {getOnboardingLink.isPending ? "Loading…" : "Stripe Dashboard"}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleConnectStripe}
                disabled={createConnectAccount.isPending || getOnboardingLink.isPending}
                className="shrink-0"
              >
                <CreditCard className="mr-1 h-3.5 w-3.5" />
                {createConnectAccount.isPending || getOnboardingLink.isPending
                  ? "Loading…"
                  : connectStatus?.account_id
                  ? "Complete Setup"
                  : "Connect Bank Account"}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Total Amenities" value={amenities.filter(a => a.is_active).length} icon={PhBuildings} iconWeight="duotone" variant="default" subtitle="Active" />
        <StatCard title="Total Bookings" value={totalBookings} icon={PhUsers} iconWeight="duotone" variant="default" />
        <StatCard title="Revenue Collected" value={`$${totalRevenue.toFixed(2)}`} icon={PhCurrencyDollar} iconWeight="duotone" variant="success" subtitle="Paid bookings" />
        <StatCard title="Pending Approval" value={pendingBookings.length} icon={PhClock} iconWeight="duotone" variant="warning" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">My Amenities</TabsTrigger>
          <TabsTrigger value="bookings">
            All Bookings
            {pendingBookings.length > 0 && (
              <Badge variant="destructive" className="ml-1.5 text-[10px] px-1.5 py-0 h-4">
                {pendingBookings.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>

        {/* ── Tab: Amenities list ── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {amenitiesLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : amenities.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-12">
                <Building2 className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No amenities yet. Add your first amenity above.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {amenities.map((a) => {
                const rev = revenue.find(r => r.amenity_id === a.id);
                return (
                  <Card key={a.id} className={`relative ${!a.is_active ? "opacity-50" : ""}`}>
                    <div
                      className="absolute top-0 left-0 bottom-0 w-1 rounded-l-lg"
                      style={{ backgroundColor: a.color }}
                    />
                    <CardContent className="pl-5 py-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{a.name}</p>
                          {a.location && (
                            <p className="text-xs text-muted-foreground">{a.location}</p>
                          )}
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            <Badge variant="outline" className="text-[10px]">
                              {a.capacity} max
                            </Badge>
                            {a.requires_payment ? (
                              <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
                                {a.price_flat != null ? `$${a.price_flat} flat` : `$${a.price_per_hour}/hr`}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">Free</Badge>
                            )}
                            {a.require_approval ? (
                              <Badge variant="secondary" className="text-[10px]">Approval</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-green-700 border-green-300">Auto</Badge>
                            )}
                            {!a.is_active && (
                              <Badge variant="destructive" className="text-[10px]">Inactive</Badge>
                            )}
                          </div>
                          {rev && (
                            <p className="text-[11px] text-muted-foreground mt-1.5">
                              {rev.total_bookings} bookings · ${Number(rev.total_revenue).toFixed(2)} earned
                            </p>
                          )}
                        </div>
                        <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => openEditAmenity(a)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {a.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{a.description}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Tab: All Bookings ── */}
        <TabsContent value="bookings" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Booking Requests</CardTitle>
              <CardDescription className="text-xs">
                All bookings for amenities you manage. Approve or deny pending requests.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myBookingsLoading ? (
                <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
              ) : myBookings.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No bookings yet for your amenities.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Amenity</TableHead>
                      <TableHead className="text-xs">Guest</TableHead>
                      <TableHead className="text-xs">Date & Time</TableHead>
                      <TableHead className="text-xs">Guests</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Payment</TableHead>
                      <TableHead className="text-xs">Amount</TableHead>
                      <TableHead className="text-xs w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myBookings.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell className="text-xs font-medium">{b.amenity?.name ?? "—"}</TableCell>
                        <TableCell className="text-xs">{b.booker?.full_name ?? b.title}</TableCell>
                        <TableCell className="text-xs">
                          {format(parseISO(b.start_time), "MMM d, h:mm a")}
                          <br />
                          <span className="text-muted-foreground">to {format(parseISO(b.end_time), "h:mm a")}</span>
                        </TableCell>
                        <TableCell className="text-xs">{b.guest_count}</TableCell>
                        <TableCell><StatusBadge status={b.status} /></TableCell>
                        <TableCell><PaymentStatusBadge status={b.payment_status} /></TableCell>
                        <TableCell className="text-xs">
                          {b.payment_amount > 0 ? `$${b.payment_amount.toFixed(2)}` : "—"}
                        </TableCell>
                        <TableCell>
                          {b.status === "pending" && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] text-green-700 border-green-300 px-2"
                                onClick={() => approveBooking.mutate(b.id)}
                                disabled={approveBooking.isPending}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-0.5" /> OK
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] text-red-700 border-red-300 px-2"
                                onClick={() => { setRejectTarget(b); setRejectReason(""); }}
                                disabled={rejectBooking.isPending}
                              >
                                <XCircle className="h-3 w-3 mr-0.5" /> No
                              </Button>
                            </div>
                          )}
                          {b.status === "approved" && (
                            <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                              <CheckCircle2 className="h-3 w-3" /> Approved
                            </span>
                          )}
                          {b.rejection_reason && (
                            <span className="text-[10px] text-muted-foreground" title={b.rejection_reason}>
                              Denied
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Revenue ── */}
        <TabsContent value="revenue" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card>
              <CardContent className="py-4 px-4">
                <p className="text-xs text-muted-foreground">Total Collected</p>
                <p className="text-2xl font-bold text-emerald-700">${totalRevenue.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">CAD from paid bookings</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <p className="text-xs text-muted-foreground">Pending Collection</p>
                <p className="text-2xl font-bold text-amber-700">${pendingRevenue.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">Awaiting payment confirmation</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 px-4">
                <p className="text-xs text-muted-foreground">Payout Status</p>
                <p className="text-sm font-semibold mt-1">
                  {connectStatus?.onboarded
                    ? <span className="text-emerald-700">✓ Active</span>
                    : <span className="text-amber-700">⚠ Not Connected</span>}
                </p>
                <p className="text-xs text-muted-foreground">
                  {connectStatus?.onboarded
                    ? "Funds transfer automatically to your bank"
                    : "Connect bank account to receive payouts"}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Revenue by Amenity</CardTitle>
            </CardHeader>
            <CardContent>
              {revenue.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No revenue data yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Amenity</TableHead>
                      <TableHead className="text-xs text-right">Bookings</TableHead>
                      <TableHead className="text-xs text-right">Paid</TableHead>
                      <TableHead className="text-xs text-right">Revenue</TableHead>
                      <TableHead className="text-xs text-right">Pending</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {revenue.map((r) => (
                      <TableRow key={r.amenity_id}>
                        <TableCell className="text-sm font-medium">{r.amenity_name}</TableCell>
                        <TableCell className="text-sm text-right">{r.total_bookings}</TableCell>
                        <TableCell className="text-sm text-right">{r.paid_bookings}</TableCell>
                        <TableCell className="text-sm text-right font-semibold text-emerald-700">
                          ${Number(r.total_revenue).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-sm text-right text-amber-700">
                          ${Number(r.pending_revenue).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold border-t-2">
                      <TableCell className="text-sm">Total</TableCell>
                      <TableCell className="text-sm text-right">{totalBookings}</TableCell>
                      <TableCell className="text-sm text-right">
                        {revenue.reduce((s, r) => s + Number(r.paid_bookings), 0)}
                      </TableCell>
                      <TableCell className="text-sm text-right text-emerald-700">
                        ${totalRevenue.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-right text-amber-700">
                        ${pendingRevenue.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {!connectStatus?.onboarded && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Action Required:</strong> Connect your bank account to receive amenity booking payments.
                Until then, guests can still book but payment collection is disabled.
                <br />
                <Button
                  size="sm"
                  className="mt-2"
                  onClick={handleConnectStripe}
                  disabled={createConnectAccount.isPending || getOnboardingLink.isPending}
                >
                  <CreditCard className="mr-1.5 h-3.5 w-3.5" /> Connect Bank Account via Stripe
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Create / Edit Amenity Dialog ──────────────────────────────────── */}
      <Dialog open={showAmenityDialog} onOpenChange={setShowAmenityDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAmenity ? "Edit Amenity" : "Add New Amenity"}</DialogTitle>
            <DialogDescription>
              This amenity will be bookable by guests, tenants, and other org members.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Amenity Name *</Label>
                <Input className="mt-1" placeholder="e.g. Rooftop Terrace, Pool, Gym"
                  value={amenityForm.name}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Location</Label>
                <Input className="mt-1" placeholder="e.g. 3rd floor, Rooftop"
                  value={amenityForm.location}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, location: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Max Capacity</Label>
                <Input type="number" min={1} className="mt-1"
                  value={amenityForm.capacity}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, capacity: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea className="mt-1" rows={2} placeholder="Brief description"
                value={amenityForm.description}
                onChange={(e) => setAmenityForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Max Booking Hours</Label>
                <Input type="number" min={1} max={24} className="mt-1"
                  value={amenityForm.max_hours}
                  onChange={(e) => setAmenityForm((p) => ({ ...p, max_hours: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {COLORS.map((c) => (
                    <button key={c} type="button" aria-label={`Select color ${c}`}
                      onClick={() => setAmenityForm((p) => ({ ...p, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 transition-all ${amenityForm.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Rules for guests (optional)</Label>
              <Textarea className="mt-1" rows={2} placeholder="e.g. No smoking, clean up after use..."
                value={amenityForm.rules}
                onChange={(e) => setAmenityForm((p) => ({ ...p, rules: e.target.value }))} />
            </div>
            <Separator />
            <div className="space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Booking Settings</p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Require manual approval</p>
                  <p className="text-xs text-muted-foreground">You review each booking request</p>
                </div>
                <Switch checked={amenityForm.require_approval}
                  onCheckedChange={(v) => setAmenityForm((p) => ({ ...p, require_approval: v }))} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Charge a booking fee</p>
                  <p className="text-xs text-muted-foreground">Guests pay via Stripe; money goes to your bank</p>
                </div>
                <Switch checked={amenityForm.requires_payment}
                  onCheckedChange={(v) => setAmenityForm((p) => ({ ...p, requires_payment: v }))} />
              </div>
              {amenityForm.requires_payment && (
                <div className="pl-4 border-l-2 border-primary/20 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Price per hour (CAD)</Label>
                      <Input type="number" min={0} step={0.01} placeholder="25.00" className="mt-1"
                        value={amenityForm.price_per_hour}
                        onChange={(e) => setAmenityForm((p) => ({ ...p, price_per_hour: e.target.value, price_flat: "" }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Flat fee (CAD)</Label>
                      <Input type="number" min={0} step={0.01} placeholder="50.00" className="mt-1"
                        value={amenityForm.price_flat}
                        onChange={(e) => setAmenityForm((p) => ({ ...p, price_flat: e.target.value, price_per_hour: "" }))} />
                    </div>
                  </div>
                  {!connectStatus?.onboarded && (
                    <Alert className="border-amber-200 bg-amber-50 py-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                      <AlertDescription className="text-xs text-amber-800">
                        Connect your bank account in the Revenue tab to receive payments.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAmenityDialog(false)}>Cancel</Button>
            {editingAmenity && (
              <Button variant="destructive"
                onClick={async () => { await deleteAmenity.mutateAsync(editingAmenity.id); setShowAmenityDialog(false); }}
                disabled={deleteAmenity.isPending}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            )}
            <Button onClick={handleSaveAmenity} disabled={manageAmenity.isPending || !amenityForm.name.trim()}>
              {manageAmenity.isPending ? "Saving…" : editingAmenity ? "Save Changes" : "Create Amenity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reject Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) setRejectTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Deny Booking</DialogTitle>
            <DialogDescription>Provide a reason (optional but helpful for the guest).</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="e.g. Conflicting event already scheduled..."
            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={rejectBooking.isPending}
              onClick={async () => {
                if (rejectTarget) {
                  await rejectBooking.mutateAsync({ id: rejectTarget.id, reason: rejectReason || undefined });
                  setRejectTarget(null);
                }
              }}>
              Confirm Denial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
